/* ═══════════════════════════════════════════════════════════════
   ALTERLY — Studio App Header
   Branding, project title, undo/redo, zoom, execution status, panel toggles.
   ═══════════════════════════════════════════════════════════════ */

import React from 'react';
import {
  Undo2,
  Redo2,
  ZoomIn,
  ZoomOut,
  Maximize2,
  Download,
  PanelLeft,
  PanelRight,
  Eye,
  AlterlyStudioLogo,
} from '../common/Icons';
import { useDomainBridge } from '../../stores/domainBridge';
import { useViewportStore } from '../../stores/viewportStore';
import { serializeProject } from '../../domain/serialization';
import styles from './AppHeader.module.css';

export const AppHeader: React.FC = () => {
  const {
    domainState,
    projectName,
    setProjectName,
    undo,
    redo,
    canUndo,
    canRedo,
    isExecuting,
    executionResult,
  } = useDomainBridge();

  const {
    zoom,
    zoomIn,
    zoomOut,
    resetViewport,
    setTransform,
    shelfOpen,
    inspectorOpen,
    previewOpen,
    toggleShelf,
    toggleInspector,
    togglePreview,
  } = useViewportStore();

  const handleExportProject = () => {
    const doc = serializeProject(domainState, projectName);
    const jsonStr = JSON.stringify(doc, null, 2);
    const blob = new Blob([jsonStr], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${projectName.toLowerCase().replace(/[^a-z0-9]/g, '_')}.alterly`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const zoomPercent = Math.round(zoom * 100);

  const handleFitView = () => {
    const nodes = Object.values(domainState.canonical.nodes);
    const viewport = document.querySelector<HTMLElement>('[data-workspace-viewport]');
    if (!viewport || nodes.length === 0) {
      resetViewport();
      return;
    }

    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    for (const node of nodes) {
      const width = node.size?.width ?? 220;
      const height = node.size?.height ?? 140;
      minX = Math.min(minX, node.position.x);
      minY = Math.min(minY, node.position.y);
      maxX = Math.max(maxX, node.position.x + width);
      maxY = Math.max(maxY, node.position.y + height);
    }

    const padding = 72;
    const availableWidth = Math.max(1, viewport.clientWidth - padding * 2);
    const availableHeight = Math.max(1, viewport.clientHeight - padding * 2);
    const fittedZoom = Math.min(1, availableWidth / (maxX - minX), availableHeight / (maxY - minY));
    setTransform({
      zoom: fittedZoom,
      x: (viewport.clientWidth - (maxX - minX) * fittedZoom) / 2 - minX * fittedZoom,
      y: (viewport.clientHeight - (maxY - minY) * fittedZoom) / 2 - minY * fittedZoom,
    });
  };

  return (
    <header className={styles.header}>
      {/* Left: Branding & Project Title */}
      <div className={styles.leftSection}>
        <div className={styles.brand}>
          <AlterlyStudioLogo size={22} />
          <span className={styles.brandTitle}>ALTERLY</span>
          <span className={styles.subpathBadge}>shakl.cc/alterly</span>
        </div>

        <div className={styles.divider} />

        <input
          className={styles.projectInput}
          value={projectName}
          onChange={(e) => setProjectName(e.target.value)}
          title="Click to rename project"
        />
      </div>

      {/* Center: History, Zoom & Status */}
      <div className={styles.centerSection}>
        <button
          className={styles.toolButton}
          onClick={undo}
          disabled={!canUndo()}
          title="Undo (Cmd+Z)"
        >
          <Undo2 size={16} />
        </button>

        <button
          className={styles.toolButton}
          onClick={redo}
          disabled={!canRedo()}
          title="Redo (Cmd+Shift+Z)"
        >
          <Redo2 size={16} />
        </button>

        <div className={styles.divider} />

        <button
          className={styles.toolButton}
          onClick={zoomOut}
          title="Zoom Out"
        >
          <ZoomOut size={16} />
        </button>

        <button
          className={styles.zoomLabel}
          onClick={() => setTransform({ zoom: 1 })}
          title="Zoom to 100%"
        >
          {zoomPercent}%
        </button>

        <button
          className={styles.toolButton}
          onClick={zoomIn}
          title="Zoom In"
        >
          <ZoomIn size={16} />
        </button>

        <button
          className={styles.toolButton}
          onClick={handleFitView}
          title="Fit View"
        >
          <Maximize2 size={14} />
        </button>

        <div className={styles.divider} />

        {/* Live Execution Status */}
        <div className={styles.statusPill}>
          <span
            className={`${styles.statusDot} ${isExecuting ? styles.statusExecuting : ''}`}
          />
          <span>
            {isExecuting
              ? 'Computing...'
              : executionResult
              ? `${executionResult.durationMs}ms`
              : 'Idle'}
          </span>
        </div>
      </div>

      {/* Right: Export & Panel Toggles */}
      <div className={styles.rightSection}>
        <button
          className={styles.actionButton}
          onClick={handleExportProject}
          title="Export Project File (.alterly)"
        >
          <Download size={14} />
          <span>Export</span>
        </button>

        <div className={styles.divider} />

        <button
          className={`${styles.toolButton} ${shelfOpen ? styles.activePanelBtn : ''}`}
          onClick={toggleShelf}
          title="Toggle Node Shelf"
        >
          <PanelLeft size={16} />
        </button>

        <button
          className={`${styles.toolButton} ${previewOpen ? styles.activePanelBtn : ''}`}
          onClick={togglePreview}
          title="Toggle Output Preview"
        >
          <Eye size={16} />
        </button>

        <button
          className={`${styles.toolButton} ${inspectorOpen ? styles.activePanelBtn : ''}`}
          onClick={toggleInspector}
          title="Toggle Properties Inspector"
        >
          <PanelRight size={16} />
        </button>
      </div>
    </header>
  );
};
